import { useMemo, useState } from 'react';
import type { OvertimeAppliesWhen, OvertimeType } from '@forecastify/shared';
import { Select, Switch, TextInput, type MenuItem } from '@forecastify/ui';
import { useT, type Translate } from '../../i18n';
import { SettingsPage } from './settings-page';
import { SettingsTable, type SettingsRow } from './settings-table';
import { EditorDialog } from './editor-dialog';
import { Field } from '../card';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Beállítások → Túlóra. C48.                                                │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE NOTE UNDER THE LIST IS THE POINT OF THE SCREEN.** Editing a multiplier here changes
 * what a FUTURE claim is worth and nothing that has already been approved — P3-5, and the one thing
 * an administrator would otherwise assume the opposite of. A workspace that raised its weekend rate
 * has not just given everybody a backdated raise.
 *
 * ⚠️⚠️ **REBUILT AGAINST THE MOCKUP, 11 Sep 2026.** It was a five-column table; the mockup is a list
 * of blocks, and the difference is not decoration:
 *
 * - **The multiplier is the biggest thing on the row**, set right, at 15px. It is the number
 *   somebody opens this screen to check, and it was an 12px cell between two others.
 * - **When it applies and how many claims hold it are one sentence under the name**, so the row
 *   answers *"can I delete this"* without a column of counts that is blank three times in four.
 * - **A kebab with four verbs**, two of which had no home at all: make it the default, and
 *   activate/deactivate. Making a type the default used to mean opening the editor and finding a
 *   yes/no dropdown.
 * - **The order is draggable**, because the marking control on the time screen offers the types in
 *   it, and an order nobody can change is an accident of insertion.
 *
 * ⚠️⚠️ **AND REBUILT AGAIN ON 22 SEP, ONTO THE PRODUCT'S OWN TABLE.** *"No title, change table
 * format, remove every text except name, multiplier (default badge, status badge, and kebab menu
 * for edit and delete)."* The blocks above were a third list idiom in a product that has one:
 * `DataList` is what every other screen uses, and a settings screen that draws its own rows is a
 * screen that has to re-earn sorting, search, the empty state and the row menu by hand.
 *
 * ⚠️ **The sentence under the name is gone with them.** *Bármely nap · 3 kérelem* answered *"can I
 * delete this"*, and the kebab answers it better by disabling the item.
 */

export function appliesWhenLabel(t: Translate, when: OvertimeAppliesWhen): string {
  /* ⚠️ One `t()` per branch with a literal key. */
  if (when === 'weekend') return t('settings.overtime.when.weekend');
  if (when === 'holiday') return t('settings.overtime.when.holiday');
  return t('settings.overtime.when.any');
}

/** ⚠️ One builder, so the row's name and its kebab cannot open two different drafts. */
function draftOf(type: OvertimeType): OvertimeTypeDraft {
  return {
    id: type.id,
    name: type.name,
    multiplier: Number(type.multiplier),
    appliesWhen: type.appliesWhen,
    active: type.active,
    isDefault: type.isDefault,
    version: type.version,
  };
}

export interface OvertimeTypeDraft {
  readonly id: string | null;
  readonly name: string;
  readonly multiplier: number;
  readonly appliesWhen: OvertimeAppliesWhen;
  readonly active: boolean;
  readonly isDefault: boolean;
  readonly version: number;
}

export function OvertimeSection(props: {
  /** Until the first answer: skeleton rows and no figures. */
  readonly loading?: boolean;
  readonly types: readonly OvertimeType[];
  readonly canManage: boolean;
  readonly serverError: string | null;
  readonly onSave: (draft: OvertimeTypeDraft) => Promise<boolean>;
  readonly onDelete: (type: OvertimeType) => void;
  /** The whole set, in its new order. Absent while nothing may be managed. */
  readonly onReorder?: (ids: readonly string[]) => void;
}) {
  const t = useT();
  /* 26 Oct 2026 · a save in flight, and whether the last one was refused — the dialog stays
     open until it lands, and shows its refusal inside. */
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [draft, setDraft] = useState<OvertimeTypeDraft | null>(null);
  const [query, setQuery] = useState('');

  const byId = useMemo(() => new Map(props.types.map((one) => [one.id, one])), [props.types]);

  /**
   * ⚠️⚠️ **FOUR COLUMNS, AND THE MOCKUP'S SECOND ONE IS DELIBERATELY NAMELESS.** It carries the
   * *alapértelmezett* badge on exactly one row; a heading over a column that is blank four times
   * in five names nothing, so the reference leaves it `{}` and so does this.
   */
  const rows: SettingsRow[] = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return props.types
      .filter((one) => needle === '' || one.name.toLowerCase().includes(needle))
      .map((one) => ({
        id: one.id,
        onOpen: !props.canManage
          ? undefined
          : () => {
              setDraft(draftOf(one));
            },
        cells: [
          { kind: 'name' as const, text: one.name, icon: 'timer' as const },
          /* \u26a0\ufe0f The mockup's second column is nameless on purpose \u2014 it carries the
           *alap\u00e9rtelmezett* badge on exactly one row. */
          one.isDefault
            ? {
                kind: 'badge' as const,
                text: t('settings.overtime.default'),
                tone: 'outline' as const,
              }
            : { kind: 'blank' as const },
          { kind: 'num' as const, text: `${String(Number(one.multiplier))}\u00d7` },
          {
            kind: 'badge' as const,
            text: one.active
              ? t('settings.schedules.status.active')
              : t('settings.schedules.status.inactive'),
            tone: one.active ? ('secondary' as const) : ('outline' as const),
            color: one.active ? 'var(--status-ok)' : 'var(--text-3)',
          },
          props.canManage
            ? {
                kind: 'kebab' as const,
                ariaLabel: t('settings.overtime.editType', { name: one.name }),
                items: menuFor(one.id),
              }
            : { kind: 'blank' as const },
        ],
      }));
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `menuFor` closes over the same
       props this already depends on; listing it would rebuild every row on every render. */
  }, [props.types, props.canManage, query, t]);

  /**
   * ⚠⚠ **A FUNCTION DECLARATION, NOT A `const` ARROW, AND THAT IS THE BUG THIS FIXES.**
   * The rows are built in a `useMemo` ABOVE this, and a `const` is in its temporal dead zone until
   * the line that defines it runs — so the first render threw *"can't access lexical declaration
   * 'menuFor' before initialization"* and the screen never drew. A declaration is hoisted, so the
   * memo can call it from anywhere in the component.
   */
  function menuFor(id: string): MenuItem[] {
    const type = byId.get(id);
    if (type === undefined) return [];
    return [
      {
        label: t('common.edit'),
        icon: 'pencil',
        onSelect: () => {
          setDraft(draftOf(type));
        },
      },
      {
        label: type.isDefault
          ? t('settings.overtime.isDefault')
          : t('settings.overtime.makeDefault'),
        icon: 'circle-check',
        disabled: type.isDefault,
        onSelect: () => {
          void props.onSave({ ...draftOf(type), isDefault: true });
        },
      },
      {
        label: type.active ? t('settings.schedules.deactivate') : t('settings.schedules.activate'),
        icon: type.active ? 'moon' : 'sun',
        onSelect: () => {
          void props.onSave({ ...draftOf(type), active: !type.active });
        },
      },
      { type: 'separator' },
      {
        label: t('common.delete'),
        icon: 'trash',
        destructive: true,
        /* ⚠️ A type somebody has claimed against cannot go — the claim would lose the thing that
           says what it was worth. The menu says so before the click. */
        disabled: type.claimCount > 0,
        onSelect: () => {
          props.onDelete(type);
        },
      },
    ];
  }

  /**
   * ⚠️⚠️ **NO WIDTH CAP — round 5 item 2.** Nine settings screens capped themselves at 680,
   * 760 or 980px, so moving between them moved the table under the reader. Ünnepnapok had
   * already lost its cap and is the shape the product owner named. The FORM-only screens keep
   * theirs: a field stretched across a wide monitor is a worse form, not a wider one.
   */
  return (
    <div data-screen="settings-overtime" className="flex min-h-0 flex-1 flex-col">
      <SettingsPage
        title={t('settings.nav.overtime')}
        action={
          !props.canManage
            ? undefined
            : {
                label: t('settings.overtime.addType'),
                icon: 'plus',
                onSelect: () => {
                  setDraft({
                    id: null,
                    name: '',
                    multiplier: 1.5,
                    appliesWhen: 'any',
                    active: true,
                    /* \u26a0\ufe0f Not the default: exactly one type is, and taking it from whoever
                       holds it should be a deliberate act rather than a side effect of adding. */
                    isDefault: false,
                    version: 0,
                  });
                },
              }
        }
        error={props.serverError}
        search={query}
        onSearch={setQuery}
        searchPlaceholder={t('settings.overtime.search')}
      >
        <SettingsTable
          grid="minmax(0,1fr) 126px 64px 112px 36px"
          minWidth={700}
          columns={[
            { label: t('settings.overtime.column.name') },
            {},
            { label: t('settings.overtime.column.multiplier'), align: 'right' },
            { label: t('settings.schedules.column.status') },
            {},
          ]}
          groups={[{ rows }]}
          loading={props.loading}
          empty={{
            icon: 'timer',
            title: t('settings.overtime.empty'),
            body: t('settings.overtime.emptyBody'),
          }}
        />
      </SettingsPage>

      <EditorDialog
        open={draft !== null}
        title={
          draft?.id === null
            ? t('settings.overtime.dialog.new')
            : t('settings.overtime.dialog.edit')
        }
        width={520}
        saveDisabled={draft === null || draft.name.trim() === ''}
        onClose={() => {
          setSaveFailed(false);
          setDraft(null);
        }}
        busy={saving}
        error={saveFailed ? props.serverError : null}
        onSave={() => {
          if (draft === null) return;
          setSaving(true);
          void props.onSave(draft).then((landed) => {
            setSaving(false);
            setSaveFailed(!landed);
            if (landed) setDraft(null);
          });
        }}
      >
        {draft === null ? null : (
          <>
            {/* ⚠️ Name and multiplier share a row, as the mockup has them: the two are read
                together, and the multiplier needs a third of the width at most. */}
            <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_120px]">
              <Field label={t('settings.overtime.column.name')} required>
                <TextInput
                  value={draft.name}
                  placeholder={t('settings.overtime.namePlaceholder')}
                  onChange={(event) => {
                    setDraft({ ...draft, name: event.target.value });
                  }}
                />
              </Field>
              <Field label={t('settings.overtime.column.multiplier')}>
                <TextInput
                  value={String(draft.multiplier)}
                  inputMode="decimal"
                  className="tabular-nums"
                  onChange={(event) => {
                    /* ⚠️ A comma is a decimal point here — Hungarian keyboards produce one, and
                       the hour parser makes the same allowance for the same reason. */
                    setDraft({
                      ...draft,
                      multiplier: Number(event.target.value.replace(',', '.')) || 0,
                    });
                  }}
                />
              </Field>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('settings.overtime.column.when')}>
                <Select
                  aria-label={t('settings.overtime.column.when')}
                  value={draft.appliesWhen}
                  onChange={(event) => {
                    const next = event.target.value;
                    setDraft({
                      ...draft,
                      appliesWhen: next === 'weekend' || next === 'holiday' ? next : 'any',
                    });
                  }}
                  options={[
                    { value: 'any', label: t('settings.overtime.when.any') },
                    { value: 'weekend', label: t('settings.overtime.when.weekend') },
                    { value: 'holiday', label: t('settings.overtime.when.holiday') },
                  ]}
                />
              </Field>
              <Field label={t('settings.schedules.status')}>
                <Select
                  aria-label={t('settings.schedules.status')}
                  value={draft.active ? 'active' : 'inactive'}
                  onChange={(event) => {
                    setDraft({ ...draft, active: event.target.value === 'active' });
                  }}
                  options={[
                    { value: 'active', label: t('settings.schedules.status.active') },
                    { value: 'inactive', label: t('settings.schedules.status.inactive') },
                  ]}
                />
              </Field>
            </div>

            {/* ⚠️ A switch with its consequence beside it, not a yes/no dropdown — *default* is a
                thing that happens when somebody marks a weekday's hours, and the row says so. */}
            <div className="flex items-center gap-2.5 rounded-lg border p-2.5">
              <span className="min-w-0 flex-1">
                <span className="block text-[12.5px] font-medium">
                  {t('settings.overtime.default')}
                </span>
              </span>
              <Switch
                checked={draft.isDefault}
                label={t('settings.overtime.default')}
                onCheckedChange={(checked) => {
                  setDraft({ ...draft, isDefault: checked });
                }}
              />
            </div>
          </>
        )}
      </EditorDialog>
    </div>
  );
}
